export type MenuListSortDirection = "asc" | "desc" | null;
export type MenuListSortDirectionsById = Record<string, MenuListSortDirection>;

export function getSortDirectionForId(
	map: MenuListSortDirectionsById,
	id: string
): MenuListSortDirection {
	return map[id] ?? null;
}

export function buildMenuListViewKey(
	...parts: Array<string | null | undefined | MenuListSortDirectionsById | Record<string, string>>
): string {
	return parts
		.map((part) =>
			part && typeof part === "object" ? JSON.stringify(part) : part ?? ""
		)
		.join(":");
}

export function getSortableName(item: { name?: unknown; display_name?: unknown }): string {
	if (typeof item.name === "string") return item.name;
	if (item.name && typeof item.name === "object") return JSON.stringify(item.name);
	if (typeof item.display_name === "string") return item.display_name;
	return "";
}

export function sortByName<T extends { name?: unknown }>(
	items: T[],
	direction: "asc" | "desc" = "asc"
): T[] {
	const sorted = [...items].sort((a, b) =>
		getSortableName(a).localeCompare(getSortableName(b), undefined, {
			sensitivity: "base",
			numeric: true,
		})
	);
	return direction === "desc" ? sorted.reverse() : sorted;
}

export function hasPendingSorts(
	categorySortDirections: MenuListSortDirectionsById,
	itemSortDirections: MenuListSortDirectionsById
): boolean {
	return (
		Object.values(categorySortDirections).some(Boolean) ||
		Object.values(itemSortDirections).some(Boolean)
	);
}

/** Bakes the per-row A–Z / Z–A view sorts into original-schema menus (`menus[].categories[].dishes`). */
export function applyPendingSorts<
	M extends { _id: string; categories: C[] },
	C extends { _id: string; name?: unknown; dishes: D[] },
	D extends { name?: unknown }
>(
	menus: M[],
	categorySortDirections: MenuListSortDirectionsById,
	itemSortDirections: MenuListSortDirectionsById
): M[] {
	return menus.map((menu) => {
		const categoryDirection = getSortDirectionForId(categorySortDirections, menu._id);
		const categories = (categoryDirection
			? sortByName(menu.categories, categoryDirection)
			: menu.categories
		).map((category) => {
			const itemDirection = getSortDirectionForId(itemSortDirections, category._id);
			return itemDirection
				? { ...category, dishes: sortByName(category.dishes || [], itemDirection) }
				: category;
		});
		return { ...menu, categories };
	});
}

export function getIndexById<T extends { _id: string }>(
	items: T[],
	id: string
): number {
	return items.findIndex((item) => item._id === id);
}

export function filterByName<T extends { name?: unknown; display_name?: unknown }>(
	items: T[],
	filterText: string
): T[] {
	const needle = filterText.trim().toLowerCase();
	if (!needle) return items;
	return items.filter((item) => {
		const haystack = `${getSortableName(item)} ${typeof item.display_name === "string" ? item.display_name : ""}`.toLowerCase();
		return haystack.includes(needle);
	});
}

export function applyMenuListView<T extends { name?: unknown; display_name?: unknown }>(
	items: T[],
	options: {
		filterText?: string;
		sortDirection?: MenuListSortDirection;
		enabled?: boolean;
	}
): T[] {
	if (options.enabled === false) return items;
	let result = items;
	if (options.filterText?.trim()) {
		result = filterByName(result, options.filterText);
	}
	if (options.sortDirection) {
		result = sortByName(result, options.sortDirection);
	}
	return result;
}

export function getTagId(tag: { _id?: string } | null | undefined): string {
	if (!tag) return "";
	return (tag as { id?: string; _id?: string }).id || tag._id || "";
}

export function matchesItemSearchFilter(
	item: { name?: unknown; display_name?: unknown },
	filterText: string
): boolean {
	const needle = filterText.trim().toLowerCase();
	if (!needle) return true;
	const haystack = `${getSortableName(item)} ${typeof item.display_name === "string" ? item.display_name : ""}`.toLowerCase();
	return haystack.includes(needle);
}

type SearchableMenuItem = { name?: unknown; display_name?: unknown };

export function menuHasMatchingItem(menu: unknown, filterText: string): boolean {
	if (!filterText.trim()) return true;
	const record = menu as {
		items?: SearchableMenuItem[];
		categories?: Array<{ dishes?: SearchableMenuItem[] }>;
	};
	if (record.items?.length) {
		return record.items.some((item) => matchesItemSearchFilter(item, filterText));
	}
	return (record.categories ?? []).some((category) =>
		(category.dishes ?? []).some((dish) => matchesItemSearchFilter(dish, filterText))
	);
}

export function categoryHasMatchingItem(
	category: unknown,
	filterText: string,
	menuItems?: Array<{ name?: unknown; display_name?: unknown; category_id?: string }>,
	categoryId?: string
): boolean {
	if (!filterText.trim()) return true;
	if (menuItems && categoryId) {
		return menuItems
			.filter((item) => item.category_id === categoryId)
			.some((item) => matchesItemSearchFilter(item, filterText));
	}
	const record = category as { dishes?: SearchableMenuItem[] };
	return (record.dishes ?? []).some((dish) => matchesItemSearchFilter(dish, filterText));
}

export function canShowListSortControls(childCount: number): boolean {
	return childCount > 1;
}

export function getMenuListFilterPlaceholder(tab: string): string {
	switch (tab) {
		case "0":
		case "menu":
			return "Search Item";
		case "1":
		case "optionSets":
			return "Filter Option Sets";
		case "2":
		case "itemTags":
			return "Filter Item Tags";
		default:
			return "Filter";
	}
}
